// Machine à états des prises (spec anti-perte phase 1, §5.2.1) : décision PURE,
// sans dépendance à Capacitor ni au réseau. Le runner (A5) et les écrans (A8, A9)
// exécutent ce que cette fonction décide ; elle ne fait jamais elle-même d'appel
// natif ni réseau.
import { isFeedbackMeta, parseTakeMeta } from "./take-meta";
import type { TakeErrorInfo, TakeManifest } from "./take-types";

/** Tentatives de fusion sur le téléphone avant le dernier recours serveur. */
export const MAX_PHONE_MERGE_ATTEMPTS = 2;
/** Délai avant la 2ᵉ tentative de fusion, dans la même session. */
export const MERGE_RETRY_DELAY_MS = 5_000;
/** Tolérance de durée par segment (1 trame AAC à 16 kHz, spec §5.3). */
export const DURATION_TOLERANCE_MS_PER_SEGMENT = 64;

/** Vue minimale de la ligne `captures` côté serveur, telle que lue par le runner. */
export interface ServerCaptureView {
  status: string;
  durationS: number | null;
  audioPath: string | null;
  /** Audio illisible estimé par le serveur (ms) ; `null` si aucun. */
  missingAudioMs: number | null;
  /**
   * Durée MESURÉE du fichier publié par `assemble-capture`
   * (`assembly_manifest.assembledMs`), écrite par le serveur seul à la
   * réussite ; `null` sans assemblage réussi. SEULE preuve qui libère une
   * prise confiée au serveur : `duration_s` peut avoir été écrite par l'app
   * (mode « complete », C25) et le statut remis à `ready` par une autre
   * écriture serveur, sans que l'audio complet soit publié.
   */
  assembledMs: number | null;
}

/** Ce que l'exécuteur sait de la prise et du serveur au moment de décider. */
export interface TakeDecisionContext {
  isActive: boolean;
  server: ServerCaptureView | null;
  /** La lecture de la ligne a RÉUSSI sous le compte de la prise. */
  serverChecked: boolean;
  /**
   * Seconde preuve d'une capture supprimée : ni `<userId>/<captureId>.m4a` ni
   * segment dans `<userId>/<captureId>/segments/` (lecture Storage réussie).
   * Une ligne absente seule ne suffit pas : la RLS de projet peut masquer la
   * ligne de son propre créateur. Absent = non prouvé.
   */
  serverAudioAbsent?: boolean;
}

export type TakeAction =
  | { kind: "close_and_merge" }
  | { kind: "retry_merge"; delayMs: number }
  | { kind: "create_capture" }
  | { kind: "confirm_upload" }
  | { kind: "hand_off" }
  | { kind: "await_server" }
  | { kind: "release" }
  | { kind: "trash" }
  | { kind: "offer_resume" }
  | { kind: "none" };

export type TakeScreenState =
  | { kind: "preparing"; conservedMs: number; needsNetwork: boolean }
  // `source` : dernier recours serveur en échec (`assembly`), ou capture de la
  // prise refusée à l'envoi pour une raison qui ne passera pas seule (`sync`).
  | { kind: "problem"; conservedMs: number; source: "assembly" | "sync" }
  | { kind: "hidden" };

/**
 * Durée conservée à ce jour : le plus grand de la durée fusionnée et de la
 * somme des segments (mesurés, ou estimés à 32 kbps s'ils ne le sont pas).
 * Une fusion ratée (C10 : plus courte que ses segments, 24-09) ou qui a
 * perdu un segment illisible ne fait jamais baisser la durée annoncée, ni
 * celle qu'exige la libération (I1, I2).
 */
export function conservedMs(m: TakeManifest): number {
  const segmentsMs = m.segments.reduce((total, seg) => {
    if (typeof seg.durationMs === "number") return total + seg.durationMs;
    // Segment pas encore mesuré (fusion interrompue) ou illisible : estimation à 32 kbps.
    return total + (seg.bytes ?? 0) / 4;
  }, 0);
  return Math.max(m.merged?.durationMs ?? 0, segmentsMs);
}

// Capture supprimée par la personne : ligne lue absente ET aucun audio dans son
// dossier Storage (deux preuves). Corbeille, jamais destruction (I1).
function captureProvedDeleted(ctx: TakeDecisionContext): boolean {
  return !ctx.server && ctx.serverChecked && ctx.serverAudioAbsent === true;
}

function decideHandedToServer(m: TakeManifest, ctx: TakeDecisionContext): TakeAction {
  if (!ctx.server) return captureProvedDeleted(ctx) ? { kind: "trash" } : { kind: "none" };
  if (ctx.server.status === "assembling") return { kind: "await_server" };
  if (ctx.server.status === "assembly_failed") return { kind: "none" };

  // Audio déclaré manquant par le serveur : la prise garde ses segments, même si
  // la marge de tolérance « couvrirait » la différence.
  if ((ctx.server.missingAudioMs ?? 0) > 0) return { kind: "none" };
  const toleranceMs = DURATION_TOLERANCE_MS_PER_SEGMENT * m.segments.length;
  const assembledMs = ctx.server.assembledMs;
  // Libération sur la seule preuve du serveur : la durée qu'il a MESURÉE sur le
  // fichier qu'il a publié (jamais `duration_s`, que l'app écrit elle-même en
  // mode « complete »).
  if (
    ctx.server.audioPath != null &&
    assembledMs != null &&
    assembledMs >= conservedMs(m) - toleranceMs
  ) {
    return { kind: "release" };
  }
  // Pas de preuve, ou audio serveur plus court que ce qui est conservé : on garde tout (I1, I2).
  return { kind: "none" };
}

/**
 * Prise d'un avis vocal (C30) : l'écran de l'avis la libère après l'envoi ou la
 * met à la corbeille à l'abandon. Aucune autre pièce n'y touche : ni action, ni
 * carte, ni proposition d'en faire une capture.
 */
export function isFeedbackTake(m: TakeManifest): boolean {
  return isFeedbackMeta(m.meta);
}

/** Prochaine étape pour une prise, sans jamais rien exécuter elle-même. */
export function decideTakeAction(m: TakeManifest, ctx: TakeDecisionContext): TakeAction {
  if (ctx.isActive || isFeedbackTake(m)) return { kind: "none" };

  switch (m.state) {
    case "released":
      return { kind: "none" };
    case "recording": {
      // Rencontre non active : offre de reprise (comportement actuel de `recoverCold`).
      // Idée flash ou métadonnées illisibles/absentes : on ferme et on fusionne.
      const meta = parseTakeMeta(m.meta);
      return meta?.type === "rencontre" ? { kind: "offer_resume" } : { kind: "close_and_merge" };
    }
    case "merging":
      // App tuée pendant la fusion : pas de reprise possible, on refait la fusion.
      return { kind: "close_and_merge" };
    case "merge_failed":
      return m.mergeAttempts < MAX_PHONE_MERGE_ATTEMPTS
        ? { kind: "retry_merge", delayMs: MERGE_RETRY_DELAY_MS }
        : { kind: "hand_off" };
    case "merged":
      return ctx.server ? { kind: "confirm_upload" } : { kind: "create_capture" };
    case "uploading":
      return { kind: "hand_off" };
    case "handed_to_server":
      return decideHandedToServer(m, ctx);
    case "retained":
      // Capture supprimée par la personne : corbeille (7 jours), jamais une
      // recréation ni un nouvel envoi. Sinon, rien à faire.
      return captureProvedDeleted(ctx) ? { kind: "trash" } : { kind: "none" };
  }
}

/** État affiché à l'écran pour une prise (indépendant de l'action à exécuter). */
export function takeScreenState(
  m: TakeManifest,
  ctx: { online: boolean; server: ServerCaptureView | null },
): TakeScreenState {
  // Prise en cours d'enregistrement : l'écran d'enregistrement actuel s'en occupe,
  // jamais « En préparation » (spec §6).
  if (m.active === true || isFeedbackTake(m)) return { kind: "hidden" };

  if (m.state === "released" || m.state === "retained") return { kind: "hidden" };

  if (m.state === "handed_to_server") {
    if (ctx.server?.status === "assembly_failed") {
      return { kind: "problem", conservedMs: conservedMs(m), source: "assembly" };
    }
    // Ligne pas encore lue (`server === null`) : le serveur avance seul, pas de
    // réseau nécessaire côté téléphone — on ne masque que si la ligne EST lue et
    // que son statut n'est plus « assembling ».
    if (ctx.server && ctx.server.status !== "assembling") return { kind: "hidden" };
  }

  if (m.state === "merged" && ctx.server) return { kind: "hidden" };

  // Capture de la prise refusée à l'envoi (C20) : « Problème signalé » tant
  // qu'elle n'est pas arrivée — l'exécuteur rappelle le signalement.
  if (m.state === "merged" && hasSyncProblem(m)) {
    return { kind: "problem", conservedMs: conservedMs(m), source: "sync" };
  }

  return {
    kind: "preparing",
    conservedMs: conservedMs(m),
    needsNetwork: !ctx.online && waitsForNetwork(m),
  };
}

// L'étape suivante de la prise passe par le réseau : envoi en cours, capture
// à envoyer, ou dernier recours serveur (fusions sur le téléphone épuisées).
function waitsForNetwork(m: TakeManifest): boolean {
  if (m.state === "uploading" || m.state === "merged") return true;
  return m.state === "merge_failed" && m.mergeAttempts >= MAX_PHONE_MERGE_ATTEMPTS;
}

// ─── Échec de synchro de la capture d'une prise (C20, tour 2) ─────────────────
// L'écran de capture classe l'échec d'envoi de la capture d'une prise et
// l'inscrit au manifeste (`lastError`, domaine `sync`) : l'exécuteur et la
// notice d'accueil le lisent même écran fermé. Le natif ne sait pas effacer
// `lastError` : une classification levée s'écrit avec le code `cleared`.

/** Domaine de `lastError` qui porte la classification d'un échec de synchro. */
export const SYNC_ERROR_DOMAIN = "sync";

/** Codes : 0 classification levée (capture arrivée), 1 panne réseau, 2 problème. */
export const SYNC_ERROR_CODE = { cleared: 0, network: 1, problem: 2 } as const;

/** Longueur maximale du motif technique inscrit (comme le natif, 300). */
const SYNC_ERROR_MAX = 300;

export function syncFailureError(cls: "network" | "problem", detail: string): TakeErrorInfo {
  return {
    domain: SYNC_ERROR_DOMAIN,
    code: SYNC_ERROR_CODE[cls],
    message: detail.slice(0, SYNC_ERROR_MAX),
  };
}

export function syncClearedError(): TakeErrorInfo {
  return {
    domain: SYNC_ERROR_DOMAIN,
    code: SYNC_ERROR_CODE.cleared,
    message: "capture arrivée : classification levée",
  };
}

/** Échec de synchro (réseau ou problème) encore inscrit au manifeste. */
export function hasSyncFailure(m: TakeManifest): boolean {
  return m.lastError?.domain === SYNC_ERROR_DOMAIN && m.lastError.code !== SYNC_ERROR_CODE.cleared;
}

/** Refus d'envoi qui ne passera pas seul : « Problème signalé », signalement rappelé. */
export function hasSyncProblem(m: TakeManifest): boolean {
  return m.lastError?.domain === SYNC_ERROR_DOMAIN && m.lastError.code === SYNC_ERROR_CODE.problem;
}
